import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
    Alert,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    Payment as PaymentIcon,
    Sync as SyncIcon,
    Refresh as RefreshIcon,
    CreditCard as CreditCardIcon,
    Celebration as CelebrationIcon
} from '@mui/icons-material';
import subscriptionApi from '../services/subscriptionApi';
import dayjs from 'dayjs';
import * as Sentry from "@sentry/react";

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount / 100);
};

const formatCredits = (credits) => {
    return new Intl.NumberFormat('en-US').format(credits);
};

const getDisplayPlanName = (planName) => {
    const name = planName?.toLowerCase() || '';
    if (name.includes('basic')) return 'Basic';
    if (name.includes('pro') && !name.includes('test')) return 'Growth';
    if (name.includes('enterprise')) return 'Pro';
    return planName;
};

// Plan change status mapping
const STATUS_STEPS = [
    { 
        key: 'pending', 
        label: 'Processing Payment', 
        description: 'Charging your payment method...',
        icon: <PaymentIcon />,
        color: 'primary'
    },
    { 
        key: 'processing', 
        label: 'Updating Subscription', 
        description: 'Applying plan changes and allocating credits...',
        icon: <SyncIcon />,
        color: 'primary'
    },
    { 
        key: 'completed', 
        label: 'Plan Changed Successfully', 
        description: 'Your subscription has been updated!',
        icon: <CelebrationIcon />,
        color: 'success'
    }
];

const getStatusStep = (status) => {
    switch (status?.toLowerCase()) {
        case 'pending':
            return 0;
        case 'processing':
            return 1;
        case 'completed':
            return 2;
        case 'failed':
        case 'rolled_back':
            return -1; // Error state
        default:
            return 0;
    }
};

const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'completed':
            return 'success';
        case 'failed':
        case 'rolled_back':
            return 'error';
        case 'pending':
        case 'processing':
            return 'primary';
        default:
            return 'default';
    }
};

const PlanChangeStatus = ({ 
    open,
    onClose,
    planChangeId,
    onComplete,
    onError,
    onRetry
}) => {
    const [status, setStatus] = useState(null);
    const [polling, setPolling] = useState(true);
    const [error, setError] = useState(null);
    const [retryLoading, setRetryLoading] = useState(false);

    // Poll for status updates
    const pollStatus = useCallback(async () => {
        if (!planChangeId || !polling) return;

        try {
            const statusData = await subscriptionApi.getPlanChangeStatus(planChangeId);
            setStatus(statusData);

            // Stop polling if completed or failed
            if (['completed', 'failed', 'rolled_back'].includes(statusData.status?.toLowerCase())) {
                setPolling(false);
                
                if (statusData.status?.toLowerCase() === 'completed') {
                    // For already completed status, show success immediately without delay
                    // For status that just became completed, add short delay to show success message
                    const delay = status ? 1500 : 0; // No delay if this is first status check
                    
                    setTimeout(() => {
                        if (onComplete) onComplete(statusData);
                    }, delay);
                } else {
                    if (onError) onError(statusData);
                }
            }
        } catch (error) {
            console.error('Error polling plan change status:', error);
            setError(error.message);
            setPolling(false);
        }
    }, [planChangeId, polling, onComplete, onError, status]);

    // Start polling when dialog opens
    useEffect(() => {
        if (open && planChangeId) {
            setPolling(true);
            setError(null);
            pollStatus();
        }
    }, [open, planChangeId, pollStatus]);

    // Polling interval
    useEffect(() => {
        if (!polling) return;

        const interval = setInterval(pollStatus, 2000); // Poll every 2 seconds
        return () => clearInterval(interval);
    }, [polling, pollStatus]);

    const handleRetryPayment = async () => {
        if (!planChangeId || !onRetry) return;

        try {
            setRetryLoading(true);
            setError(null);
            
            await subscriptionApi.retryPlanChangePayment(planChangeId);
            
            // Restart polling after retry
            setPolling(true);
            
            if (onRetry) onRetry();
        } catch (error) {
            Sentry.captureException(error);
            setError(error.message || 'Failed to retry payment');
            console.error('Error retrying payment:', error);
        } finally {
            setRetryLoading(false);
        }
    };

    const handleClose = () => {
        setPolling(false);
        if (onClose) onClose();
    };

    const currentStep = getStatusStep(status?.status);
    const isCompleted = status?.status?.toLowerCase() === 'completed';
    const isFailed = ['failed', 'rolled_back'].includes(status?.status?.toLowerCase());

    return (
        <Dialog
            open={open}
            onClose={isFailed ? handleClose : undefined} // Only allow close if failed
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1,
                background: isCompleted ? 
                    'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)' :
                    isFailed ?
                        'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)' :
                        'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isCompleted ? <CelebrationIcon /> : 
                     isFailed ? <ErrorIcon /> : 
                     <ScheduleIcon />}
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isCompleted ? 'Plan Changed!' :
                         isFailed ? 'Payment Failed' :
                         'Changing Plan...'}
                    </Typography>
                </Box>
                {isFailed && (
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {status && (
                    <Box>
                        {/* Plan Change Summary */}
                        <Card sx={{ mb: 3, background: 'rgba(0,0,0,0.02)' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    Plan Change Summary
                                </Typography>
                                
                                <List dense sx={{ p: 0 }}>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText
                                            primary="New Plan"
                                            secondary={getDisplayPlanName(status.new_plan_name)}
                                        />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(status.new_plan_price_cents)}/month
                                        </Typography>
                                    </ListItem>
                                    
                                    {status.immediate_charge_cents > 0 && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText
                                                primary="Immediate Charge"
                                                secondary="Amount charged today"
                                            />
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                {formatCurrency(status.immediate_charge_cents)}
                                            </Typography>
                                        </ListItem>
                                    )}
                                    
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText
                                            primary="Additional Credits Added"
                                            secondary="Extra credits added to your account"
                                        />
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                                            {formatCredits(status.credits_allocated || 0)}
                                        </Typography>
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>

                        {/* Status Indicator */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Chip
                                    label={status.status?.toUpperCase() || 'PROCESSING'}
                                    color={getStatusColor(status.status)}
                                    icon={
                                        isCompleted ? <CheckCircleIcon /> :
                                        isFailed ? <ErrorIcon /> :
                                        <ScheduleIcon />
                                    }
                                />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {isCompleted ? 'Plan change completed successfully!' :
                                     isFailed ? 'Payment processing failed' :
                                     'Processing your plan change...'}
                                </Typography>
                            </Box>

                            {/* Progress bar for pending/processing states */}
                            {!isCompleted && !isFailed && (
                                <LinearProgress 
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        background: 'rgba(0,0,0,0.1)'
                                    }} 
                                />
                            )}
                        </Box>

                        {/* Steps Progress */}
                        {!isFailed && (
                            <Stepper activeStep={currentStep} orientation="vertical">
                                {STATUS_STEPS.map((step, index) => (
                                    <Step key={step.key}>
                                        <StepLabel
                                            icon={
                                                index < currentStep ? <CheckCircleIcon color="success" /> :
                                                index === currentStep ? step.icon : 
                                                <ScheduleIcon color="disabled" />
                                            }
                                        >
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    fontWeight: index <= currentStep ? 600 : 400,
                                                    color: index <= currentStep ? 'text.primary' : 'text.secondary'
                                                }}
                                            >
                                                {step.label}
                                            </Typography>
                                        </StepLabel>
                                        <StepContent>
                                            <Typography variant="body2" color="text.secondary">
                                                {step.description}
                                            </Typography>
                                        </StepContent>
                                    </Step>
                                ))}
                            </Stepper>
                        )}

                        {/* Error State */}
                        {isFailed && (
                            <Alert 
                                severity="error" 
                                sx={{ mb: 2 }}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={handleRetryPayment}
                                        disabled={retryLoading}
                                        startIcon={retryLoading ? <SyncIcon /> : <RefreshIcon />}
                                    >
                                        {retryLoading ? 'Retrying...' : 'Retry Payment'}
                                    </Button>
                                }
                            >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {status.status === 'rolled_back' ? 
                                        'Payment failed and changes have been rolled back. No charges were applied.' :
                                        'Payment processing failed. Please try again or update your payment method.'
                                    }
                                </Typography>
                            </Alert>
                        )}

                        {/* Success Information */}
                        {isCompleted && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Your plan has been successfully changed! Credits have been added to your account 
                                    and your next billing date is {dayjs(status.next_billing_date).format('MMM D, YYYY')}.
                                </Typography>
                            </Alert>
                        )}

                        {/* Processing Information */}
                        {!isCompleted && !isFailed && (
                            <Alert severity="info">
                                <Typography variant="body2">
                                    Please don't close this window. We're processing your plan change and will notify you when it's complete.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                {isCompleted && (
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        startIcon={<CheckCircleIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                            borderRadius: 2
                        }}
                        fullWidth
                        size="large"
                    >
                        Continue
                    </Button>
                )}

                {isFailed && (
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button
                            onClick={handleClose}
                            sx={{ borderRadius: 2 }}
                            size="large"
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleRetryPayment}
                            variant="contained"
                            disabled={retryLoading}
                            startIcon={retryLoading ? <SyncIcon /> : <CreditCardIcon />}
                            sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                flex: 1
                            }}
                            size="large"
                        >
                            {retryLoading ? 'Retrying Payment...' : 'Retry Payment'}
                        </Button>
                    </Box>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PlanChangeStatus; 